import { useEffect, useState } from "react";
import api, { withSite, formatErr } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import ExportButton from "@/components/ExportButton";
import PdfButton from "@/components/PdfButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { GraduationCap } from "lucide-react";

// Local (not UTC) calendar date so AU morning shifts don't default to yesterday.
function todayLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function Training() {
  const { activeSite } = useSite();
  const { user } = useAuth();
  const canEdit = user?.role !== "auditor";

  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    staff_name: "",
    course: "Food Safety Supervisor",
    completion_date: todayLocal(),
    expiry_date: "",
    certificate_no: "",
    health_declaration_signed: false,
    notes: "",
  });

  const load = async () => {
    if (!activeSite) return;
    const { data } = await api.get("/training", withSite(activeSite.id));
    setItems(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [activeSite]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/training", form, withSite(activeSite.id));
      toast.success("Training record saved");
      setForm({ ...form, staff_name: "", certificate_no: "", notes: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="animate-fade-in" data-testid="training-page">
      <PageHeader
        title="Staff Training & Hygiene"
        subtitle="Maintain a register of Food Safety Supervisor certificates, training completion and signed health declarations."
        action={
          <div className="flex items-center gap-2">
            <ExportButton kind="training" />
            <PdfButton kind="training" />
          </div>
        }
      />

      {canEdit && (
        <form onSubmit={submit} className="surface p-6 md:p-8 mb-8 grid md:grid-cols-3 gap-5">
          <div className="md:col-span-3 flex items-center gap-2">
            <GraduationCap className="w-5 h-5 text-eucalyptus-700" />
            <h2 className="font-display font-bold text-lg">New training record</h2>
          </div>

          <div className="space-y-2">
            <Label>Staff member</Label>
            <Input data-testid="tr-name" value={form.staff_name}
              onChange={(e) => setForm({ ...form, staff_name: e.target.value })} className="h-12" required />
          </div>
          <div className="space-y-2">
            <Label>Course</Label>
            <Input data-testid="tr-course" value={form.course}
              onChange={(e) => setForm({ ...form, course: e.target.value })} className="h-12" />
          </div>
          <div className="space-y-2">
            <Label>Certificate #</Label>
            <Input data-testid="tr-cert" value={form.certificate_no}
              onChange={(e) => setForm({ ...form, certificate_no: e.target.value })} className="h-12" />
          </div>
          <div className="space-y-2">
            <Label>Completion date</Label>
            <Input data-testid="tr-completion" type="date" value={form.completion_date}
              onChange={(e) => setForm({ ...form, completion_date: e.target.value })} className="h-12" required />
          </div>
          <div className="space-y-2">
            <Label>Expiry</Label>
            <Input data-testid="tr-expiry" type="date" value={form.expiry_date}
              onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} className="h-12" />
          </div>
          <div className="flex items-center gap-3 pt-7">
            <Checkbox
              id="health"
              data-testid="tr-health"
              checked={form.health_declaration_signed}
              onCheckedChange={(v) =>
                setForm({ ...form, health_declaration_signed: Boolean(v) })
              }
            />
            <label htmlFor="health" className="text-sm font-medium">
              Health declaration signed
            </label>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button
              type="submit"
              data-testid="tr-submit"
              className="h-12 bg-eucalyptus-900 hover:bg-eucalyptus-700 text-white"
            >
              Save record
            </Button>
          </div>
        </form>
      )}

      <div className="surface overflow-hidden">
        <div className="grid grid-cols-12 bg-bone-alt px-6 py-3 text-[11px] uppercase font-bold tracking-widest text-eucalyptus-700">
          <div className="col-span-3">Staff</div>
          <div className="col-span-3">Course</div>
          <div className="col-span-2">Completed</div>
          <div className="col-span-2">Expiry</div>
          <div className="col-span-2">Health decl.</div>
        </div>
        {items.length === 0 ? (
          <div className="p-10 text-center text-charcoal-soft text-sm">No records yet.</div>
        ) : (
          items.map((r) => (
            <div key={r.id} className="grid grid-cols-12 px-6 py-4 border-t border-[color:var(--border)] items-center">
              <div className="col-span-3 font-medium">{r.staff_name}</div>
              <div className="col-span-3 text-sm">{r.course}</div>
              <div className="col-span-2 font-mono text-xs">{r.completion_date || "—"}</div>
              <div className="col-span-2 font-mono text-xs">{r.expiry_date || "—"}</div>
              <div className="col-span-2 text-sm">
                {r.health_declaration_signed ? "Signed" : "—"}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
